import { FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { usePermissions } from '@mint-lib/profile-helpers';

import {
  StaticCurrencySpan,
  StaticNumericSpan,
  StaticPercentSpan,
} from '@myn/mui';

import { ValueType } from '../../../../../../../types/_campaigns/campaignBuilder.js';
import { getDecimalLimit } from '../../GoalField/utils.js';
import { GoalFieldProps } from '../types.js';
import TextVariant from './TextVariant.jsx';

type Props = Omit<
  GoalFieldProps,
  'column' | 'row' | 'overriddenDecimalLimit' | 'variant'
>;

const GoalVariant: FC<Props> = ({ currency, value, scale, color }) => {
  const { t } = useTranslation('@myn/planning');
  const { getPermissionField } = usePermissions();
  const isNonFinancialUser = !getPermissionField('financialDataFeature');

  const { goalLabel, goalValue, goalValueType, isLocked } = value;

  const isGoalOptionSelected = !!goalLabel && !!goalValueType;
  const isCurrencyValueHidden =
    isNonFinancialUser && goalValueType === ValueType.DECIMAL;
  const isNumericValue = goalValueType === ValueType.INT;
  const isPercentageValue = goalValueType === ValueType.PERCENT;
  const isCurrencyValue = goalValueType === ValueType.DECIMAL && !!currency;

  if (isLocked) {
    return (
      <TextVariant scale={scale} color={color}>
        {t('Row Locked')}
      </TextVariant>
    );
  }

  if (!isGoalOptionSelected) {
    return null;
  }

  if (isCurrencyValueHidden) {
    return (
      <TextVariant
        scale={scale}
        color={color}
      >{`${goalLabel}: ---`}</TextVariant>
    );
  }

  const goalValueDecimalLimit = getDecimalLimit(goalValueType);
  const staticSpanProps = {
    number: goalValue ?? 0,
    options: {
      minDecimals: goalValueDecimalLimit,
      maxDecimals: goalValueDecimalLimit,
    },
  };

  if (isNumericValue) {
    return (
      <TextVariant scale={scale} color={color}>
        {`${goalLabel}: `}
        <StaticNumericSpan {...staticSpanProps} />
      </TextVariant>
    );
  }

  if (isPercentageValue) {
    return (
      <TextVariant scale={scale} color={color}>
        {`${goalLabel}: `}
        <StaticPercentSpan {...staticSpanProps} />
      </TextVariant>
    );
  }

  if (isCurrencyValue) {
    return (
      <TextVariant scale={scale} color={color}>
        {`${goalLabel}: `}
        <StaticCurrencySpan currency={currency} {...staticSpanProps} />
      </TextVariant>
    );
  }

  return (
    <TextVariant scale={scale} color={color}>
      {`${goalLabel}: `}
      <StaticNumericSpan {...staticSpanProps} />
    </TextVariant>
  );
};

export default GoalVariant;
